import { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { googleCloudStorageService, UploadResult, UploadProgress } from '../utils/googleCloudStorageService';

// Validation function for image files
const validateImageFiles = (files: File[]) => {
  const valid: File[] = [];
  const invalid: { file: File; reason: string }[] = [];
  
  files.forEach(file => {
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      invalid.push({ file, reason: 'File size exceeds 10MB limit' });
      return;
    }
    
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      invalid.push({ file, reason: 'Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed' });
      return;
    }
    
    valid.push(file);
  });
  
  return { valid, invalid };
};

interface ImageUploadProps {
  onImagesUploaded: (urls: string[]) => void;
  maxImages?: number;
  existingImages?: string[];
}

interface UploadingImage {
  file: File;
  progress: number;
  result?: UploadResult;
}

const ImageUpload = ({ onImagesUploaded, maxImages = 10, existingImages = [] }: ImageUploadProps) => {
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (files: FileList | File[]) => {
    const { valid, invalid } = validateImageFiles(Array.from(files));
    
    // Show errors for invalid files
    invalid.forEach(({ file, reason }) => {
      console.error(`${file.name}: ${reason}`);
    });

    if (valid.length === 0) return;

    // Check if we exceed max images
    const totalImages = existingImages.length + valid.length;
    if (totalImages > maxImages) {
      alert(`Máximo ${maxImages} imágenes permitidas. Tienes ${existingImages.length} y estás intentando agregar ${valid.length}.`);
      return;
    }

    uploadImages(valid);
  };

  const uploadImages = async (files: File[]) => {
    // Initialize uploading state
    const initialUploading = files.map(file => ({
      file,
      progress: 0
    }));
    
    setUploadingImages(initialUploading);

    try {
      const uploadPromises = files.map(async (file, index) => {
        const result = await googleCloudStorageService.uploadImage(
          file,
          (progress: UploadProgress) => {
            setUploadingImages(prev => 
              prev.map((item, i) => 
                i === index 
                  ? { ...item, progress: progress.percentage }
                  : item
              )
            );
          }
        );

        setUploadingImages(prev => 
          prev.map((item, i) => 
            i === index 
              ? { ...item, result }
              : item
          )
        );

        return result;
      });

      const results = await Promise.all(uploadPromises);
      
      // Get successful uploads
      const successfulUploads = results
        .filter((result: UploadResult) => result.success && result.url)
        .map((result: UploadResult) => result.url!);

      if (successfulUploads.length > 0) {
        onImagesUploaded(successfulUploads);
      }

      // Clear uploading state after a delay
      setTimeout(() => {
        setUploadingImages([]);
      }, 2000);

    } catch (error) {
      console.error('Error uploading images:', error);
      setUploadingImages([]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileSelect(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect(files);
    }
    // Reset input
    e.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const canUploadMore = existingImages.length < maxImages;

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {canUploadMore && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-900 mb-2">
            Sube imágenes del vehículo
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Arrastra y suelta imágenes aquí o haz clic para seleccionar
          </p>
          <button
            type="button"
            onClick={triggerFileInput}
            className="btn-primary"
          >
            Seleccionar Imágenes
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Máximo {maxImages} imágenes, 5MB por imagen. JPG, PNG, WebP, GIF
          </p>
        </div>
      )}

      {/* Upload Progress */}
      {uploadingImages.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Subiendo imágenes...</h4>
          {uploadingImages.map((upload, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                {upload.result ? (
                  upload.result.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )
                ) : (
                  <ImageIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {upload.file.name}
                </p>
                
                {!upload.result && (
                  <div className="mt-1">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${upload.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.round(upload.progress)}%
                    </p>
                  </div>
                )}
                
                {upload.result && (
                  <p className={`text-xs mt-1 ${
                    upload.result.success ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {upload.result.success ? 'Subida exitosa' : upload.result.error}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Limit Warning */}
      {!canUploadMore && (
        <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Has alcanzado el límite máximo de {maxImages} imágenes.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;